import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { VerificationProcess } from '@/types/credentials'


export const useCredentialStore = defineStore('credentials', () => {
  // Verification process state
  const process = ref<VerificationProcess>({
    payment_processing: false,
    payment_confirmed: false,
    submitted_credentials: false,
    processing_credentials: false,
    invalid_credentials: null,
    awaiting_token: false,
    blessed_tx: null,
  })

  // Loading states
  const loading = ref({
    awaitingToken_deposit: false,
    awaitingToken_did: false,
    processingPayment: false,
    processingVerification: false,
    checkingVerificationStatus: false,
  })

  // User identity state
  const userId = ref<string | null>(null)
  const userPkh = ref<string | null>(null)
  const userAddress = ref<string | null>(null)
  const networkId = ref<number>(0)
  const vendingPolicyId = ref<string>('381865cd16bc2591990ebf0c160af589a9aee60a39a24b6e8ad0e078')

  // Wallet assets related to credentials
  const depositoryNotes = ref<Array<{ assetName: string; quantity: number; policyId: string }>>([])
  const didTokens = ref<Array<{ assetName: string; quantity: number; policyId: string }>>([])

  // Computed
  const hasDepositoryNotes = computed(() => depositoryNotes.value.length > 0)
  const hasDidToken = computed(() => didTokens.value.length > 0)
  const isVerified = computed(() => hasDidToken.value)
  const blessedTx = computed(() => process.value.blessed_tx)

  // Actions
  function updateProcess(updates: Partial<VerificationProcess>) {
    process.value = { ...process.value, ...updates }
  }

  function updateAccountInfo(accountInfo: {
    user_id?: string
    activeProcesses?: Array<{ name: string; details: any }>
  }) {
    if (accountInfo.user_id) {
      userId.value = accountInfo.user_id
    }

    // Reset process state, then apply active processes from backend
    process.value = {
      payment_processing: false,
      payment_confirmed: false,
      submitted_credentials: false,
      processing_credentials: false,
      invalid_credentials: null,
      awaiting_token: false,
      blessed_tx: null,
    }

    if (accountInfo.activeProcesses) {
      for (const activeProcess of accountInfo.activeProcesses) {
        if (activeProcess.name === 'id_verification') {
          process.value = { ...process.value, ...activeProcess.details }
        }
      }
    }
  }

  function setWalletInfo(info: {
    address: string
    pkh: string
    networkId: number
  }) {
    userAddress.value = info.address
    userPkh.value = info.pkh
    networkId.value = info.networkId
  }

  function updateAssets(assets: Record<string, Record<string, number>>) {
    // Search for Depository Notes and DID tokens under vending policy
    const found_deposit: typeof depositoryNotes.value = []
    const found_did: typeof didTokens.value = []

    for (const [policyId, policyAssets] of Object.entries(assets)) {
      if (policyId.match(vendingPolicyId.value) || vendingPolicyId.value === null) {
        for (const [assetName, quantity] of Object.entries(policyAssets)) {
          if (assetName.substring(0, 4) === 'Depo') {
            found_deposit.push({ assetName, quantity, policyId })
          }
          if (assetName.substring(0, 4) === 'DID_') {
            found_did.push({ assetName, quantity, policyId })
          }
        }
      }
    }

    depositoryNotes.value = found_deposit
    didTokens.value = found_did
  }

  function resetLoading() {
    loading.value = {
      awaitingToken_deposit: false,
      awaitingToken_did: false,
      processingPayment: false,
      processingVerification: false,
      checkingVerificationStatus: false,
    }
  }

  function $reset() {
    process.value = {
      payment_processing: false,
      payment_confirmed: false,
      submitted_credentials: false,
      processing_credentials: false,
      invalid_credentials: null,
      awaiting_token: false,
      blessed_tx: null,
    }
    resetLoading()
    depositoryNotes.value = []
    didTokens.value = []
    userId.value = null
    userPkh.value = null
    userAddress.value = null
  }

  return {
    // State
    process,
    loading,
    userId,
    userPkh,
    userAddress,
    networkId,
    vendingPolicyId,
    depositoryNotes,
    didTokens,
    // Computed
    hasDepositoryNotes,
    hasDidToken,
    isVerified,
    blessedTx,
    // Actions
    updateProcess,
    updateAccountInfo,
    setWalletInfo,
    updateAssets,
    resetLoading,
    $reset,
  }
})
